import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Award, Users, DollarSign, ArrowRight } from 'lucide-react';
import './ResourceGuide.css';

function CaseStudies() {
  const caseStudies = [
    {
      icon: <TrendingUp size={32} />,
      business: 'Local Coffee Shop',
      challenge: 'Lost customers to big chains and delivery apps',
      solution: 'Built branded app and loyalty program',
      results: [
        '+$35,000/month in direct sales',
        '62% reduction in 3rd party fees',
        '1,200 app downloads in 3 months',
        '4.8 star rating'
      ]
    },
    {
      icon: <Award size={32} />,
      business: 'Family-Owned Restaurant',
      challenge: 'Couldn\'t compete with restaurant delivery apps',
      solution: 'Launched online ordering with AI-powered website',
      results: [
        '+$50,000/month in first 6 months',
        '300% increase in repeat orders',
        'Owns 100% of customer data',
        'Expanded to 2 more locations'
      ]
    },
    {
      icon: <Users size={32} />,
      business: 'Service Business (Plumbing)',
      challenge: 'Couldn\'t get leads without expensive ads',
      solution: 'Local SEO + AI phone assistant',
      results: [
        'Page 1 Google rankings in 3 months',
        '+40 qualified leads per month',
        'AI assistant books 60% of calls',
        'Saved $2,000/month on Google Ads'
      ]
    },
    {
      icon: <DollarSign size={32} />,
      business: 'Retail Store',
      challenge: 'Missed out-of-hours sales opportunities',
      solution: 'Mobile app + online ordering system',
      results: [
        '+$20,000/month after hours',
        '15% increase in total revenue',
        'Better customer data for marketing',
        'Waitlist during peak hours eliminated'
      ]
    }
  ];

  return (
    <div className="guide-container" role="main">
      <div className="guide-header-section">
        <Link to="/resources" className="back-link">← Back to Resources</Link>
        <h1>Case Studies: Real Success Stories</h1>
        <p className="guide-subtitle">Learn how businesses like yours grew revenue using our platform</p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>From Struggling to Thriving</h2>
          <p>
            The businesses below were losing customers to competitors and third-party apps. By taking control of their 
            online presence, they transformed their business-and their bottom line.
          </p>
        </section>

        <section className="guide-section">
          <div className="portfolio-callout">
            <div className="portfolio-callout-copy">
              <span className="portfolio-callout-label">Live Portfolio</span>
              <h2>Want to see real client sites instead of sample numbers?</h2>
              <p>
                Browse our live portfolio to see active launches, real visuals, and the kind of client-facing work we are shipping right now.
              </p>
            </div>
            <Link to="/portfolio" className="portfolio-callout-link">
              View Live Portfolio <ArrowRight size={18} />
            </Link>
          </div>
        </section>

        <section className="guide-section">
          <h2>Real Results, Real Numbers</h2>
          <div className="case-studies-grid">
            {caseStudies.map((study, index) => (
              <div key={index} className="case-study-card">
                <div className="study-icon">{study.icon}</div>
                <h3>{study.business}</h3>
                
                <div className="study-section">
                  <h4>The Challenge</h4>
                  <p>{study.challenge}</p>
                </div>

                <div className="study-section">
                  <h4>What They Did</h4>
                  <p>{study.solution}</p>
                </div>

                <div className="study-results">
                  <h4>The Results</h4>
                  <ul>
                    {study.results.map((result, resultIndex) => (
                      <li key={resultIndex}>✓ {result}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="guide-section">
          <h2>Common Themes from Our Top Performers</h2>
          <div className="themes-section">
            <div className="theme-card">
              <h3>🎯 They Owned Their Customer Relationships</h3>
              <p>
                Instead of relying on delivery apps, social media, or Google ads, they built direct customer relationships 
                through email, SMS, and mobile apps. This gave them control and reduced dependency on any one channel.
              </p>
            </div>

            <div className="theme-card">
              <h3>💰 They Took Back Revenue</h3>
              <p>
                By moving away from 30% commission delivery apps, they kept more money. That money went back into their 
                business-hiring, expansion, or reinvestment in customer experience.
              </p>
            </div>

            <div className="theme-card">
              <h3>⭐ They Invested in Customer Experience</h3>
              <p>
                The best performers didn't just go online. They thought about every touchpoint: website design, ordering 
                experience, loyalty rewards, and customer support. Excellence compounds.
              </p>
            </div>

            <div className="theme-card">
              <h3>📊 They Measured Everything</h3>
              <p>
                They tracked what worked and doubled down on it. They used data to make decisions about marketing, 
                pricing, products, and customer retention.
              </p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>What Type of Business Gets the Biggest ROI?</h2>
          <div className="best-fit">
            <div className="fit-card">
              <h3>🏆 Best Fit</h3>
              <ul>
                <li>Restaurants & Food Businesses</li>
                <li>Service Businesses (Plumbing, HVAC, etc.)</li>
                <li>Retail Stores</li>
                <li>Hair & Beauty</li>
                <li>Gyms & Fitness</li>
              </ul>
              <p><strong>Why:</strong> High repeat customer potential + commission-heavy current model = huge ROI</p>
            </div>

            <div className="fit-card">
              <h3>💡 Also Great Fit</h3>
              <ul>
                <li>Medical Practices</li>
                <li>Legal Services</li>
                <li>Home Services</li>
                <li>Professional Services</li>
              </ul>
              <p><strong>Why:</strong> Customers actively searching + local service area = strong local SEO results</p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Your Success Path</h2>
          <div className="success-steps">
            <div className="success-step">
              <div className="step-number">1</div>
              <h4>Month 1: Launch & Learn</h4>
              <p>Get your online presence up. The goal is to start generating online sales, even small ones.</p>
            </div>

            <div className="success-step">
              <div className="step-number">2</div>
              <h4>Month 2-3: Optimize</h4>
              <p>Look at what's working. Are customers ordering? Are they visiting your site? Focus on what's working.</p>
            </div>

            <div className="success-step">
              <div className="step-number">3</div>
              <h4>Month 4-6: Scale</h4>
              <p>With initial success, invest in marketing and customer acquisition. Your online business should grow 20-30% MoM.</p>
            </div>

            <div className="success-step">
              <div className="step-number">4</div>
              <h4>Month 6+: Leverage</h4>
              <p>Your online business is now a solid revenue stream. You can diversify into new products, services, or locations.</p>
            </div>
          </div>
        </section>

        <section className="guide-cta">
          <h2>Ready to Write Your Success Story?</h2>
          <p>These businesses started exactly where you are. Learn how our platform helps you succeed.</p>
          <Link to="/features" className="guide-cta-btn">See Our Platform <ArrowRight size={18} /></Link>
        </section>
      </div>

      <div className="guide-navigation">
        <Link to="/resources/growth-tips" className="next-guide">
          Next: Growth Tips <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
}

export default CaseStudies;
